import React, { useState, useEffect } from "react";

const OfferDetails = ({
  offer,
  btnShortlist = false,
  btnRemoveShortlist = false,
  btnSendQuery = false,
  btnRequestQuote = false,
  btnRequestSpecifications = false,
  btnRequestBestPrice = false,
  btnNegotiate = false,
  btnRequestShippingDetails = false,
  btnLogisticsSupport = false,
  btnRaiseQuery = false,
}) => {
  const [activeTab, setActiveTab] = useState("otherInfo");
  const [INRToUsd, setINRToUSD] = useState(0.012); // USD conversion rate
  const [marginPercentage, setMarginPercentage] = useState(0);
  const [isMarginChanged, setIsMarginChanged] = useState(false);

  // Initialize marginPercentage from offer data
  useEffect(() => {
    if (offer) {
      const initialMargin = calculateMarginPercentage(offer);
      setMarginPercentage(initialMargin || 0);
    }
  }, [offer]);
  // Format price to 2 decimal places
  const formatPrice = (value) => {
    if (!value || value === "") return "0.00";
    const numValue = parseFloat(value);
    return isNaN(numValue) ? "0.00" : numValue.toFixed(2);
  };

  // Convert INR to USD
  const convertToUSD = (inrValue) => {
    if (!inrValue || inrValue === "") return "0.00";
    const usdValue = parseFloat(inrValue) * INRToUsd;
    return isNaN(usdValue) ? "0.00" : usdValue.toFixed(2);
  };

  // Format date to DD/MM/YYYY format
  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;

      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();

      return `${day}/${month}/${year}`;
    } catch (error) {
      return dateString;
    }
  };
  function formatData(data) {
    if (typeof data === "string" && isNaN(data))
      return parseFloat(data).toFixed(2);
    return data.toFixed(2);
  }
  // Handle tab click for offer details
  const handleTabClick = (tab) => {
    setActiveTab(tab);
  };
  // Calculate per MT prices
  const calculatePerMTPrice = (totalPrice, quantity) => {
    if (!totalPrice || !quantity || quantity === 0) return "0.00";
    const perMTPrice = parseFloat(totalPrice) / parseFloat(quantity);
    return isNaN(perMTPrice) ? "0.00" : perMTPrice.toFixed(2);
  };

  // Calculate margin percentage from offer data
  const calculateMarginPercentage = (offer) => {
    return offer.Margin && offer.BasePrice
      ? Math.round(((offer.Margin * 100) / offer.BasePrice) * 100) / 100
      : offer.MarginPercentage;
  };

  // Calculate runtime pricing values
  const calculatePricingValues = () => {
    if (!offer) {
      return { FOBSalePrice: 0, ExportDuty: 0, CIFSalePrice: 0 };
    }

    const margin = (offer.BasePrice * marginPercentage) / 100;
    const FOBSalePrice = offer.BasePrice + margin;
    const ExportDuty = (FOBSalePrice * (offer.ExportDutyPercentage || 0)) / 100;
    const CIFSalePrice =
      FOBSalePrice +
      ExportDuty +
      (offer.ShippingInsurance || 0) +
      (offer.GST || 0);

    return { FOBSalePrice, ExportDuty, CIFSalePrice };
  };

  return (
    <>
      <section className="product-details2">
        <div className="row">
          <div className="col-lg-12 col-sm-12 col-12">
            <div className="tabs">
              <button
                className={activeTab === "otherInfo" ? "active" : ""}
                onClick={() => handleTabClick("otherInfo")}
              >
                OFFER INFORMATION
              </button>
              <button
                className={activeTab === "specifications" ? "active" : ""}
                onClick={() => handleTabClick("specifications")}
              >
                Commodity Details
              </button>
              <button
                className={activeTab === "qtypkg" ? "active" : ""}
                onClick={() => handleTabClick("qtypkg")}
              >
                Quantity & Packaging
              </button>
              <button
                className={activeTab === "shipping" ? "active" : ""}
                onClick={() => handleTabClick("shipping")}
              >
                Delivery & Logistic
              </button>
              <button
                className={activeTab === "pricing" ? "active" : ""}
                onClick={() => handleTabClick("pricing")}
              >
                Pricing/Payment/Guarantee
              </button>
              <button
                className={activeTab === "breakup" ? "active" : ""}
                onClick={() => handleTabClick("breakup")}
              >
                Price Breakup
              </button>
              <button
                className={activeTab === "others" ? "active" : ""}
                onClick={() => handleTabClick("others")}
              >
                Others
              </button>
            </div>
          </div>
        </div>

        {activeTab === "otherInfo" && (
          <div className="tab-content active">
            <div className="row">
              <div className="col-lg-12 col-sm-9 col-6">
                <div className="row">
                  <div className="col-lg-3 col-sm-4 col-6">
                    <label>Offer No.</label>
                    <input
                      type="text"
                      readOnly
                      value={offer.OfferSequenceID || ""}
                      className="form-control"
                    />
                  </div>
                  <div className="col-lg-3 col-sm-4 col-6">
                    <label>Offer Type</label>
                    <input
                      type="text"
                      readOnly
                      value={offer.OfferType || ""}
                      className="form-control"
                    />
                  </div>
                  <div className="col-lg-3 col-sm-4 col-6">
                    <label htmlFor="offerStatus">Offer Status</label>
                    <input
                      id="offerStatus"
                      type="text"
                      value={offer?.OfferStatus || "Indicative"}
                      className="form-control"
                      readOnly
                    />
                  </div>
                  <div className="col-lg-3 col-sm-4 col-6">
                    <label>Creation Date</label>
                    <input
                      type="text"
                      readOnly
                      value={formatDate(offer.CreatedAt || "")}
                      className="form-control"
                    />
                  </div>
                  <div className="col-lg-3 col-sm-4 col-6">
                    <label>Expiry Date</label>
                    <input
                      type="text"
                      readOnly
                      value={formatDate(offer.Expires || "")}
                      className="form-control"
                    />
                  </div>
                  <div className="col-lg-6 col-sm-8 col-12">
                    <label htmlFor="sellerInformation">
                      Seller Information
                    </label>
                    <input
                      id="sellerInformation"
                      type="text"
                      value={offer?.Sellerinformation || "AMTPL"}
                      className="form-control"
                      readOnly
                    />
                  </div>
                </div>
              </div>
              <div className="col-lg-3 col-sm-3 col-6">
                <div className="actions">
                  <div className="row">
                    <div className="col-12 mb-2">
                      {btnRemoveShortlist && (
                        <button className="form-control">Remove</button>
                      )}
                    </div>
                    <div className="col-12 mb-2">
                      {btnSendQuery && (
                        <button className="form-control">Send Query</button>
                      )}
                    </div>
                    <div className="col-12 mb-2">
                      {btnSendQuery && (
                        <button className="form-control">Send Query</button>
                      )}
                    </div>
                    <div className="col-12 mb-2">
                      {btnRequestQuote && (
                        <button className="form-control">Request Quote</button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === "specifications" && (
          <div className="tab-content active">
            <div className="row">
              <div className="col-lg-3 col-sm-4 col-6">
                <label>Category</label>
                <input
                  type="text"
                  readOnly
                  value={offer.CategoryName || ""}
                  className="form-control"
                />
              </div>
              <div className="col-lg-3 col-sm-4 col-6">
                <label>Product</label>
                <input
                  type="text"
                  readOnly
                  value={offer.ProductName || ""}
                  className="form-control"
                />
              </div>
              <div className="col-lg-3 col-sm-4 col-6">
                <label htmlFor="hsCode">HS Code</label>
                <input
                  id="hsCode"
                  type="text"
                  value={offer?.HsCode || ""}
                  className="form-control"
                  readOnly
                  placeholder=""
                />
              </div>
              <div className="col-lg-3 col-sm-4 col-6">
                <label>Grade</label>
                <input
                  type="text"
                  readOnly
                  value={offer.Grade || ""}
                  className="form-control"
                />
              </div>
              <div className="col-lg-3 col-sm-4 col-6">
                <label htmlFor="variety">Variety</label>
                <input
                  id="variety"
                  type="text"
                  value={offer?.Variety || ""}
                  className="form-control"
                  readOnly
                  placeholder=""
                />
              </div>
              <div className="col-lg-3 col-sm-4 col-6">
                <label>Origin Country</label>
                <input
                  type="text"
                  readOnly
                  value={offer.Country || ""}
                  className="form-control"
                />
              </div>
              <div className="col-lg-6 col-sm-6 col-6">
                <div className="row">
                  {offer.productSpecifications && (
                    <div className="col-12">
                      <label htmlFor="productSpecifications">
                        Product Specifications
                      </label>
                      <textarea
                        id="productSpecifications"
                        name="productSpecifications"
                        className="form-control"
                        rows="5"
                        value={offer.productSpecifications || ""}
                        readOnly
                      />
                    </div>
                  )}
                </div>
              </div>
              <div className="col-lg-3 col-sm-3 col-6">
                <div className="actions">
                  <div className="row">
                    <div className="col-12 mb-2">
                      {btnRequestSpecifications && (
                        <button className="form-control">
                          Request Specifications
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === "qtypkg" && (
          <div className="tab-content active">
            <div className="row">
              <div className="col-lg-9 col-sm-9 col-6">
                <div className="row">
                  <div className="col-lg-3 col-sm-4 col-6">
                    <label>Offered Quantity (MT)</label>
                    <input
                      type="text"
                      readOnly
                      value={formatPrice(offer.Quantity || "")}
                      className="form-control"
                    />
                  </div>
                  <div className="col-lg-3 col-sm-4 col-6">
                    <label htmlFor="packagingType">Packaging Type</label>
                    <select
                      id="packagingType"
                      className="form-control"
                      value={offer?.PackagingType || "Bulk"}
                      readOnly
                    >
                      <option value="Bulk">Bulk</option>
                      <option value="Bags">Bags</option>
                    </select>
                  </div>
                  <div className="col-lg-3 col-sm-4 col-6">
                    <label htmlFor="packagingSize">Packaging Size</label>
                    <input
                      id="packagingSize"
                      type="text"
                      value={offer?.PackagingSize || "50kg"}
                      className="form-control"
                      readOnly
                      placeholder="e.g., 50kg"
                    />
                  </div>
                  <div className="col-lg-9 col-sm-8 col-12">
                    <label htmlFor="additionalPackagingNotes">
                      Additional Packaging Notes
                    </label>
                    <textarea
                      id="additionalPackagingNotes"
                      value={offer?.AdditionalPackagingNotes || ""}
                      className="form-control"
                      readOnly
                      rows="3"
                    />
                  </div>
                </div>
              </div>
              <div className="col-lg-3 col-sm-3 col-6">
                <div className="actions">
                  <div className="row">
                    <div className="col-12 mb-2"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === "shipping" && (
          <div className="tab-content active">
            <div className="row">
              <div className="col-lg-9 col-sm-9 col-6">
                <div className="row">
                  <div className="col-lg-3 col-sm-4 col-6">
                    <label htmlFor="shippingMethodPreference">
                      Shipping Method Preference
                    </label>
                    <input
                      id="shippingMethodPreference "
                      type="text"
                      value={offer?.ShippingMethodPreference || "Ocean"}
                      className="form-control"
                      readOnly
                    />
                  </div>
                  <div className="col-lg-3 col-sm-4 col-6">
                    <label htmlFor="loadingPort">Loading Port</label>
                    <input
                      id="State "
                      type="text"
                      value={offer?.State || ""}
                      className="form-control"
                      readOnly
                    />
                  </div>
                  <div className="col-lg-3 col-sm-4 col-6">
                    <label htmlFor="deliveryPort">Delivery Port</label>
                    <input
                      id="deliveryPort"
                      type="text"
                      value={offer?.DischargePort || ""}
                      className="form-control"
                      readOnly
                    />
                  </div>
                  <div className="col-lg-3 col-sm-4 col-6">
                    <label htmlFor="loadingLocation">Loading Location</label>
                    <input
                      id="loadingLocation "
                      type="text"
                      value={offer?.State || ""}
                      className="form-control"
                      readOnly
                    />
                  </div>
                  <div className="col-lg-3 col-sm-4 col-6">
                    <label htmlFor="deliveryLocation">Delivery Location</label>
                    <input
                      id="deliveryLocation"
                      type="text"
                      value={offer?.DestinationCountry || ""}
                      className="form-control"
                      readOnly
                      placeholder=""
                    />
                  </div>
                  <div className="col-lg-3 col-sm-4 col-6">
                    <label htmlFor="incoterms">Incoterms</label>
                    <input
                      id="incoterms"
                      type="text"
                      value={offer?.INCO || ""}
                      className="form-control"
                      readOnly
                      placeholder=""
                    />
                  </div>
                  <div className="col-lg-3 col-sm-4 col-6">
                    <label htmlFor="transitTime">Transit Time</label>
                    <input
                      id="transitTime"
                      type="text"
                      value={offer?.TransitTime || "60 Days Approx"}
                      className="form-control"
                      readOnly
                    />
                  </div>
                  <div className="col-lg-3 col-sm-4 col-6">
                    <label htmlFor="partialDeliveryAllowed">
                      Partial Delivery Allowed?
                    </label>
                    <input
                      id="partialDeliveryAllowed"
                      type="text"
                      value={offer?.PartialDeliveryAllowed || "No"}
                      className="form-control"
                      readOnly
                      placeholder=""
                    />
                  </div>
                  <div className="col-lg-3 col-sm-4 col-6">
                    <label htmlFor="dailyUnloadingCapacity">
                      Daily Unloading Capacity
                    </label>
                    <input
                      id="dailyUnloadingCapacity"
                      type="text"
                      value={offer?.DailyUnloadingCapacity || "No"}
                      className="form-control"
                      readOnly
                    />
                  </div>
                  <div className="col-lg-3 col-sm-4 col-6">
                    <label htmlFor="unloadingResponsibility">
                      Unloading Responsibility
                    </label>
                    <input
                      id="unloadingResponsibility"
                      type="text"
                      value={offer?.UnloadingResponsibility || "Buyer"}
                      className="form-control"
                      readOnly
                    />
                  </div>
                </div>
              </div>
              <div className="col-lg-3 col-sm-3 col-6">
                <div className="actions">
                  <div className="row">
                    <div className="col-12 mb-2">
                      {btnRequestShippingDetails && (
                        <button className="form-control">
                          Request Shipping Details
                        </button>
                      )}
                    </div>
                    <div className="col-12 mb-2">
                      {btnLogisticsSupport && (
                        <button className="form-control">
                          Logistics Support
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === "pricing" && (
          <div className="tab-content active">
            <div className="row">
              <div className="col-lg-9 col-sm-9 col-6">
                <div className="row">
                  <div className="col-lg-3 col-sm-4 col-6">
                    <label>Currency</label>
                    <input
                      id="Currency "
                      type="text"
                      value={"USD"}
                      className="form-control"
                      readOnly
                    />
                  </div>
                  <div className="col-lg-3 col-sm-4 col-6">
                    <label htmlFor="fobSalePrice">
                      FOB Sale Price (Per MT)
                    </label>
                    <input
                      id="fobSalePrice"
                      type="number"
                      value={convertToUSD(
                        calculatePerMTPrice(offer.FOBSalePrice, offer.Quantity)
                      )}
                      className="form-control"
                      readOnly
                    />
                  </div>
                  <div className="col-lg-3 col-sm-4 col-6">
                    <label htmlFor="totalFobSalePrice">
                      Total FOB Sale Price
                    </label>
                    <input
                      id="totalFobSalePrice"
                      type="number"
                      value={convertToUSD(offer.FOBSalePrice || "")}
                      className="form-control"
                      readOnly
                    />
                  </div>
                </div>
              </div>
              <div className="col-lg-3 col-sm-3 col-6">
                <div className="actions">
                  <div className="row">
                    <div className="col-12 mb-2">
                      {btnRequestBestPrice && (
                        <button className="form-control">
                          Request Best Price
                        </button>
                      )}
                    </div>
                    <div className="col-12 mb-2">
                      {btnNegotiate && (
                        <button className="form-control">Negotiate</button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        {activeTab === "breakup" && offer && (
          <div className="tab-content active">
            <div className="row">
              <div className="col-lg-12 col-sm-12 col-12">
                <div className="row">
                  <label>
                    Offer Pricing Breakup for Offer: {offer?.ProductName} -{" "}
                    {offer?.Grade} - {offer?.Quantity} MT -{" "}
                    {offer?.DestinationCountry}
                  </label>
                  <table className="table table-striped table-bordered">
                    <thead>
                      <tr className="dataheader">
                        <th>Pricing Item</th>
                        <th>Pricing Component</th>
                        <th>Amount (Rs)</th>
                        <th>Amount (USD)</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="primerow1">
                        <td>Seller's Quantity (MT)</td>
                        <td className="currency right">
                          {formatData(offer?.Quantity)}
                        </td>
                        <td></td>
                        <td></td>
                      </tr>
                      <tr className="altrow">
                        <td>Seller's Price /MT (USD)</td>
                        <td className="currency right">
                          {formatData(offer?.Price)}
                        </td>
                        <td></td>
                        <td></td>
                      </tr>
                      <tr className="primerow1">
                        <td>Seller's Total Price</td>
                        <td></td>
                        <td className="currency right">
                          {formatData(offer?.Quantity * offer?.Price)}
                        </td>
                        <td className="currency right">
                          {formatPrice(
                            convertToUSD(offer?.Quantity * offer?.Price)
                          )}
                        </td>
                      </tr>
                      <tr className="altrow">
                        <td>AgroPreneur's Charges</td>
                        <td className="currency right">
                          {formatData(offer?.APCommissionPercentage)}%
                        </td>
                        <td className="currency right">
                          {formatData(offer?.APCommission)}
                        </td>
                        <td className="currency right">
                          {formatPrice(convertToUSD(offer?.APCommission))}
                        </td>
                      </tr>
                      <tr className="primerow1">
                        <td>Sorting & Grading Charges</td>
                        <td className="currency right">
                          {formatData(offer?.SortGradeChargedPercentage)}%
                        </td>
                        <td className="currency right">
                          {formatData(offer?.SortGradeCharged)}
                        </td>
                        <td className="currency right">
                          {formatPrice(convertToUSD(offer?.SortGradeCharged))}
                        </td>
                      </tr>
                      <tr className="altrow">
                        <td>Operations & Finance Charges</td>
                        <td className="currency right">
                          {formatData(offer?.OpsFinChargesPercentage)}%
                        </td>
                        <td className="currency right">
                          {formatData(offer?.OpsFinCharges)}
                        </td>
                        <td className="currency right">
                          {formatPrice(convertToUSD(offer?.OpsFinCharges))}
                        </td>
                      </tr>
                      <tr className="primerow1">
                        <td>Inland Transport Charges</td>
                        <td className="currency right">
                          {offer?.InlandTransport && offer?.Quantity
                            ? formatData(
                                offer?.InlandTransport / offer?.Quantity
                              )
                            : "0.00"}
                        </td>
                        <td className="currency right">
                          {offer?.InlandTransport
                            ? formatData(offer?.InlandTransport)
                            : "0.00"}
                        </td>
                        <td className="currency right">
                          {offer?.InlandTransport
                            ? formatPrice(convertToUSD(offer?.InlandTransport))
                            : "0.00"}
                        </td>
                      </tr>
                      <tr
                        className="primerow bold-font red-row"
                        style={{ backgroundColor: "#FFA500" }}
                      >
                        <td style={{ backgroundColor: "#FFA500" }}>
                          Seller's BASE Price
                        </td>
                        <td style={{ backgroundColor: "#FFA500" }}></td>
                        <td
                          className="currency right"
                          style={{ backgroundColor: "#FFA500" }}
                        >
                          {formatData(offer?.BasePrice)}
                        </td>
                        <td
                          className="currency right"
                          style={{ backgroundColor: "#FFA500" }}
                        >
                          {formatPrice(convertToUSD(offer?.BasePrice))}
                        </td>
                      </tr>
                      <tr className="altrow">
                        <td>Margin (%)</td>
                        <td className="currency right">
                          {formatData(marginPercentage)}%
                        </td>
                        <td className="currency right">
                          {formatData(
                            (offer?.BasePrice * marginPercentage) / 100
                          )}
                        </td>
                        <td className="currency right">
                          {formatPrice(
                            convertToUSD(
                              (offer?.BasePrice * marginPercentage) / 100
                            )
                          )}
                        </td>
                      </tr>
                      <tr className="primerow bold-font">
                        <td style={{ backgroundColor: "#FFA500" }}>
                          FOB Sale Price
                        </td>
                        <td style={{ backgroundColor: "#FFA500" }}></td>
                        <td
                          style={{ backgroundColor: "#FFA500" }}
                          className="currency right"
                        >
                          {formatData(calculatePricingValues().FOBSalePrice)}
                        </td>
                        <td
                          style={{ backgroundColor: "#FFA500" }}
                          className="currency right"
                        >
                          {formatPrice(
                            convertToUSD(calculatePricingValues().FOBSalePrice)
                          )}
                        </td>
                      </tr>
                      <tr className="altrow">
                        <td>Export Duty</td>
                        <td className="currency right">
                          {formatData(offer?.ExportDutyPercentage)}%
                        </td>
                        <td className="currency right">
                          {formatData(calculatePricingValues().ExportDuty)}
                        </td>
                        <td className="currency right">
                          {formatPrice(
                            convertToUSD(calculatePricingValues().ExportDuty)
                          )}
                        </td>
                      </tr>
                      <tr className="altrow">
                        <td>Shipping & Insurance</td>
                        <td className="currency right">
                          Rs. {formatData(offer?.ShippingInsurancePercentage)} /
                          MT
                        </td>
                        <td className="currency right">
                          {formatData(offer?.ShippingInsurance)}
                        </td>
                        <td className="currency right">
                          {formatPrice(convertToUSD(offer?.ShippingInsurance))}
                        </td>
                      </tr>
                      <tr className="altrow">
                        <td>GST (Local Taxes)</td>
                        <td></td>
                        <td className="currency right">
                          {formatData(offer?.GST)}
                        </td>
                        <td className="currency right">
                          {formatPrice(convertToUSD(offer?.GST))}
                        </td>
                      </tr>
                      <tr className="primerow bold-font">
                        <td style={{ backgroundColor: "#FFA500" }}>
                          CIF Sale Price
                        </td>
                        <td style={{ backgroundColor: "#FFA500" }}></td>
                        <td
                          style={{ backgroundColor: "#FFA500" }}
                          className="currency right"
                        >
                          {formatData(calculatePricingValues().CIFSalePrice)}
                        </td>
                        <td
                          style={{ backgroundColor: "#FFA500" }}
                          className="currency right"
                        >
                          {formatPrice(
                            convertToUSD(calculatePricingValues().CIFSalePrice)
                          )}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="col-lg-2 col-sm-3 col-12 d-flex flex-column align-items-end">
                <div className="actions">
                  <div className="row">
                    {/* <div className="col-12 mb-2">
                      <button
                        onClick={handleShortlist}
                        disabled={isMarginChanged}
                        className={`form-control ${isMarginChanged ? "disabled-button" : ""}`}
                      >
                        Shortlist
                      </button>
                    </div>
                    <div className="col-12 mb-2">
                      <button
                        onClick={handleModify}
                        disabled={!isMarginChanged}
                        className={`form-control ${!isMarginChanged ? "disabled-button" : ""}`}
                      >
                        Modify
                      </button>
                    </div>
                    <div className="col-12 mb-2">
                      <button
                        onClick={handleApprove}
                        disabled={isMarginChanged}
                        className={`form-control ${isMarginChanged ? "disabled-button" : ""}`}
                      >
                        Approve
                      </button>
                    </div>
                    <div className="col-12 mb-2">
                      <button
                        onClick={handleSendBAFO}
                        disabled={isMarginChanged}
                        className={`form-control ${isMarginChanged ? "disabled-button" : ""}`}
                      >
                        Send BAFO
                      </button>
                    </div> */}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        {activeTab === "others" && (
          <div className="tab-content active">
            <div className="row">
              <div className="col-lg-9 col-sm-9 col-6">
                <div className="row">
                  <div className="col-lg-12 col-sm-12 col-12">
                    <label htmlFor="specialInstructionsBuyer">
                      Special Instructions for Buyer
                    </label>
                    <textarea
                      id="specialInstructionsBuyer"
                      value={offer?.SpecialInstructionsBuyer || ""}
                      className="form-control"
                      readOnly
                      rows="4"
                      placeholder="Special instructions for the buyer..."
                    />
                  </div>
                </div>
              </div>
              <div className="col-lg-3 col-sm-3 col-6">
                <div className="actions">
                  <div className="row">
                    <div className="col-12 mb-2"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        {activeTab === "queries" && (
          <div className="tab-content active">
            <div className="row">
              <div className="col-lg-10 col-sm-9 col-12">
                <table>
                  <thead>
                    <tr>
                      <th>Query Date</th>
                      <th>Query</th>
                      <th>Reply Date</th>
                      <th>Reply</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td colSpan="4">No queries available for this offer</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="col-lg-2 col-sm-3 col-12">
                <div className="actions">
                  {btnRaiseQuery && (
                    <button className="form-control">Raise Query</button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </section>
    </>
  );
};

export default OfferDetails;
